// pages/Optimization.tsx
import React, { useState } from "react";
import {
  ArrowTrendingUpIcon,
  LightBulbIcon,
  ChartBarIcon,
  BoltIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";

const Optimization: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "suggestions" | "automation" | "testing"
  >("suggestions");
  const [automationEnabled, setAutomationEnabled] = useState(true);
  const [activeTests, setActiveTests] = useState([1, 3]);

  // Optimization suggestions
  const suggestions = [
    {
      id: 1,
      title: "Increase ad density",
      description: "Add more ad units to high-traffic pages",
      impact: "high",
      estimatedIncrease: "+15% revenue",
      status: "pending",
      action: "Implement",
    },
    {
      id: 2,
      title: "Mobile ad optimization",
      description: "Optimize ad sizes for mobile devices",
      impact: "medium",
      estimatedIncrease: "+8% mobile revenue",
      status: "completed",
      action: "Completed",
    },
    {
      id: 3,
      title: "Header bidding setup",
      description: "Implement header bidding for increased competition",
      impact: "high",
      estimatedIncrease: "+20% competition",
      status: "in-progress",
      action: "Continue",
    },
    {
      id: 4,
      title: "Ad refresh rate",
      description: "Adjust ad refresh timing for better performance",
      impact: "medium",
      estimatedIncrease: "+12% viewability",
      status: "pending",
      action: "Review",
    },
    {
      id: 5,
      title: "Video ad integration",
      description: "Add video ad units to increase RPM",
      impact: "high",
      estimatedIncrease: "+25% RPM",
      status: "pending",
      action: "Learn more",
    },
  ];

  // A/B Tests
  const abTests = [
    {
      id: 1,
      name: "Ad Placement Test",
      description: "Testing optimal ad positions on article pages",
      metrics: "CTR, Revenue, Viewability",
      duration: "7 days",
      progress: 65,
      isActive: true,
    },
    {
      id: 2,
      name: "Ad Size Optimization",
      description: "Comparing different ad sizes for mobile",
      metrics: "Mobile RPM, Engagement",
      duration: "14 days",
      progress: 30,
      isActive: true,
    },
    {
      id: 3,
      name: "Color Scheme Test",
      description: "Testing ad colors for better visibility",
      metrics: "CTR, Click-through rate",
      duration: "10 days",
      progress: 100,
      isActive: false,
    },
  ];

  // Automation rules
  const automationRules = [
    {
      id: 1,
      name: "Auto-pause underperforming",
      description: "Pause ad units with CTR below 0.5%",
      status: "active",
      lastTriggered: "2 hours ago",
    },
    {
      id: 2,
      name: "Revenue optimization",
      description: "Switch to higher-paying ad networks during peak hours",
      status: "active",
      lastTriggered: "Yesterday",
    },
    {
      id: 3,
      name: "Seasonal adjustments",
      description: "Increase ad density during holiday seasons",
      status: "inactive",
      lastTriggered: "Never",
    },
  ];

  const toggleTest = (id: number) => {
    setActiveTests((prev) =>
      prev.includes(id) ? prev.filter((testId) => testId !== id) : [...prev, id]
    );
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "in-progress":
        return <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-normal text-gray-900">Optimization</h1>
          <p className="text-sm text-gray-600 mt-1">
            Tools and suggestions to maximize your revenue
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Auto-optimization:</span>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={automationEnabled}
                onChange={(e) => setAutomationEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Run optimization scan
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Optimization Score</p>
              <p className="text-2xl font-normal text-gray-900">84/100</p>
            </div>
            <SparklesIcon className="h-8 w-8 text-blue-500" />
          </div>
          <div className="mt-4">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full"
                style={{ width: "84%" }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Potential Revenue Gain</p>
              <p className="text-2xl font-normal text-gray-900">+32%</p>
            </div>
            <ArrowTrendingUpIcon className="h-8 w-8 text-green-500" />
          </div>
          <p className="mt-2 text-sm text-gray-600">
            If all suggestions applied
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Tests</p>
              <p className="text-2xl font-normal text-gray-900">2</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-purple-500" />
          </div>
          <button className="mt-2 text-sm text-blue-600 hover:text-blue-700">
            View tests
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Auto-optimizations</p>
              <p className="text-2xl font-normal text-gray-900">15</p>
            </div>
            <BoltIcon className="h-8 w-8 text-yellow-500" />
          </div>
          <p className="mt-2 text-sm text-gray-600">This week</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "suggestions", label: "Suggestions", icon: LightBulbIcon },
            { id: "automation", label: "Automation", icon: BoltIcon },
            { id: "testing", label: "A/B Testing", icon: ChartBarIcon },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Suggestions Tab */}
      {activeTab === "suggestions" && (
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">Quick Actions</h3>
              <p className="text-sm text-gray-600">
                Apply these optimizations instantly
              </p>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <button className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50">
                <div className="flex items-center space-x-3">
                  <BoltIcon className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">
                      Enable Auto-refresh
                    </p>
                    <p className="text-sm text-gray-600">
                      Refresh ads every 30 seconds
                    </p>
                  </div>
                </div>
              </button>

              <button className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50">
                <div className="flex items-center space-x-3">
                  <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium text-gray-900">Add Video Ads</p>
                    <p className="text-sm text-gray-600">Increase RPM by 25%</p>
                  </div>
                </div>
              </button>

              <button className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50">
                <div className="flex items-center space-x-3">
                  <SparklesIcon className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="font-medium text-gray-900">Smart Placement</p>
                    <p className="text-sm text-gray-600">
                      AI-powered ad positioning
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Detailed Suggestions */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">
                Personalized Suggestions
              </h3>
              <p className="text-sm text-gray-600">
                Based on your site's performance
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {suggestions.map((suggestion) => (
                <div key={suggestion.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start space-x-3">
                        {getStatusIcon(suggestion.status)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-1">
                            <h4 className="font-medium text-gray-900">
                              {suggestion.title}
                            </h4>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${getImpactColor(
                                suggestion.impact
                              )}`}
                            >
                              {suggestion.impact.toUpperCase()} IMPACT
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {suggestion.description}
                          </p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{suggestion.estimatedIncrease}</span>
                            <span>•</span>
                            <span className="capitalize">
                              {suggestion.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                      {suggestion.action}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Automation Tab */}
      {activeTab === "automation" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">Automation Rules</h3>
              <p className="text-sm text-gray-600">
                Set rules for automatic optimization
              </p>
            </div>

            <div className="divide-y divide-gray-200">
              {automationRules.map((rule) => (
                <div key={rule.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <BoltIcon className="h-5 w-5 text-blue-500" />
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-medium text-gray-900">
                            {rule.name}
                          </p>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              rule.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {rule.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {rule.description}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Last triggered: {rule.lastTriggered}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button className="text-sm text-blue-600 hover:text-blue-700">
                        Edit
                      </button>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={rule.status === "active"}
                          className="sr-only peer"
                          readOnly
                        />
                        <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add New Rule */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <PlusIcon className="h-5 w-5 text-gray-400" />
              <div>
                <h4 className="font-medium text-gray-900">Create New Rule</h4>
                <p className="text-sm text-gray-600">
                  Set up custom automation rules
                </p>
              </div>
            </div>
            <button className="mt-4 w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-300 hover:bg-blue-50">
              <div className="text-center">
                <PlusIcon className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  Click to create a new automation rule
                </p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* A/B Testing Tab */}
      {activeTab === "testing" && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">A/B Tests</h3>
                  <p className="text-sm text-gray-600">
                    Test different ad configurations
                  </p>
                </div>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Create New Test
                </button>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {abTests.map((test) => (
                <div key={test.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <ChartBarIcon className="h-5 w-5 text-blue-500" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-medium text-gray-900">
                              {test.name}
                            </h4>
                            <span
                              className={`text-xs px-2 py-1 rounded-full ${
                                test.isActive
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {test.isActive ? "Active" : "Completed"}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {test.description}
                          </p>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm text-gray-500">
                              <span>Metrics: {test.metrics}</span>
                              <span>Duration: {test.duration}</span>
                            </div>

                            {/* Progress Bar */}
                            <div className="mt-2">
                              <div className="flex justify-between text-xs text-gray-500 mb-1">
                                <span>Progress</span>
                                <span>{test.progress}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${test.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ml-4 flex flex-col space-y-2">
                      <button
                        onClick={() => toggleTest(test.id)}
                        className={`px-3 py-1.5 text-sm rounded ${
                          activeTests.includes(test.id)
                            ? "bg-red-100 text-red-700 hover:bg-red-200"
                            : "bg-green-100 text-green-700 hover:bg-green-200"
                        }`}
                      >
                        {activeTests.includes(test.id) ? (
                          <>
                            <PauseIcon className="h-4 w-4 inline mr-1" />
                            Pause
                          </>
                        ) : (
                          <>
                            <PlayIcon className="h-4 w-4 inline mr-1" />
                            Resume
                          </>
                        )}
                      </button>
                      <button className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded">
                        View Results
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Test Results Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Test Results</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 mb-2">Best Performing</p>
                <p className="font-medium text-gray-900">
                  Vertical ad placement (728x90)
                </p>
                <p className="text-sm text-gray-600">
                  +24% CTR compared to horizontal placement
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 mb-2">Revenue Impact</p>
                <p className="font-medium text-gray-900">
                  Native ads perform best
                </p>
                <p className="text-sm text-gray-600">
                  +18% revenue from native ad units
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Optimization;
